import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import RecordPage from '../../components/shell/RecordPage';
import { StatTiles } from '../../components/shell/Page';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { Trophy, AlertCircle, TrendingUp, Target, BookOpen } from 'lucide-react';

export default function CoursePerformance() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [courseTitle, setCourseTitle] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [res, courseRes] = await Promise.all([
        api.get(`/courses/${courseId}/performance`),
        api.get(`/courses/${courseId}`).catch(() => ({ data: null })),
      ]);
      setData(res.data);
      setCourseTitle(courseRes.data?.title || '');
    } catch {
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  }, [api, courseId]);

  useEffect(() => {
    load();
  }, [load]);

  const backToCourse = () => navigate(`/learn/course/${courseId}`);
  const pageTitle = courseTitle ? `Performance: ${courseTitle}` : 'Mock-test performance';

  if (loading || !data) {
    return (
      <DashboardLayout>
        <RecordPage title={loading ? 'Mock-test performance' : 'No data'} loading={loading} onBack={backToCourse} findable={false} maxWidth="none">
          <p className="text-muted-foreground text-center py-12">No data</p>
        </RecordPage>
      </DashboardLayout>
    );
  }

  const r = data.interview_readiness;
  const readinessColor =
    r.status === 'ready' ? 'border-green-300 bg-green-50 text-green-700' :
    r.status === 'needs_practice' ? 'border-amber-300 bg-amber-50 text-amber-700' :
    r.status === 'weak' ? 'border-red-300 bg-red-50 text-red-700' :
    'border-gray-300 bg-gray-50 text-gray-700';

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="course-performance-page"
        title={pageTitle}
        subtitle="Aggregate analysis across all your mock-test attempts in this course"
        onBack={backToCourse}
        onRefresh={load}
        findable={false}
        maxWidth="none"
        primary={[
          { key: 'course', label: 'Back to course', icon: BookOpen, onClick: backToCourse },
        ]}
      >
        {/* Stats tiles */}
        <StatTiles
          items={[
            { label: 'Overall score', value: `${data.overall_score}%`, icon: Trophy },
            { label: 'Mock attempts', value: data.total_attempts, icon: Target },
            { label: 'Strengths', value: data.strengths.length, icon: TrendingUp },
            { label: 'Focus areas', value: data.weaknesses.length, icon: AlertCircle },
          ]}
        />

        {/* Interview Readiness */}
        <Card className={`border-2 ${readinessColor}`}>
          <CardContent className="p-5 flex items-center justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-wide opacity-70">Interview Readiness</p>
              <p className="text-2xl font-bold mt-1">{r.label}</p>
              {data.total_attempts === 0 && (
                <p className="text-sm opacity-70 mt-1">Take a mock test to see your readiness</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-4xl font-extrabold">{r.score}%</p>
            </div>
          </CardContent>
        </Card>

        {/* Topic Performance */}
        {data.topic_performance.length > 0 && (
          <Card>
            <CardHeader><CardTitle>Topic-Wise Breakdown</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {data.topic_performance.map(tp => (
                <div key={tp.topic} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">{tp.topic}</span>
                    <span className="text-muted-foreground">{tp.correct}/{tp.total} · {tp.accuracy}%</span>
                  </div>
                  <Progress value={tp.accuracy} className={`h-2 ${tp.accuracy >= 80 ? '[&>div]:bg-green-500' : tp.accuracy >= 60 ? '[&>div]:bg-amber-500' : '[&>div]:bg-red-500'}`} />
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Strengths & Weaknesses */}
        {(data.strengths.length > 0 || data.weaknesses.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.strengths.length > 0 && (
              <Card className="border-green-200 bg-green-50">
                <CardHeader className="pb-2"><CardTitle className="text-base text-green-700">💪 Your Strengths</CardTitle></CardHeader>
                <CardContent className="flex flex-wrap gap-2">
                  {data.strengths.map(s => <Badge key={s} variant="outline" className="border-green-500 text-green-700 bg-white">{s}</Badge>)}
                </CardContent>
              </Card>
            )}
            {data.weaknesses.length > 0 && (
              <Card className="border-red-200 bg-red-50">
                <CardHeader className="pb-2"><CardTitle className="text-base text-red-700">📚 Focus Areas</CardTitle></CardHeader>
                <CardContent className="flex flex-wrap gap-2">
                  {data.weaknesses.map(w => <Badge key={w} variant="outline" className="border-red-500 text-red-700 bg-white">{w}</Badge>)}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Attempts history */}
        {data.attempts.length > 0 && (
          <Card>
            <CardHeader><CardTitle>Recent Attempts</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {data.attempts.map(a => (
                  <div key={a.attempt_id} className="p-3 flex items-center justify-between hover:bg-muted/30">
                    <div>
                      <p className="font-medium text-sm">{a.exam_title}</p>
                      <p className="text-xs text-muted-foreground">{a.submitted_at ? new Date(a.submitted_at).toLocaleString() : '—'}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold">{a.percentage}%</span>
                      <Badge variant={a.passed ? 'default' : 'destructive'} className={a.passed ? 'bg-green-600' : ''}>
                        {a.passed ? 'Passed' : 'Failed'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </RecordPage>
    </DashboardLayout>
  );
}
